'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { isNativePlatform } from '@/lib/utils/platform'
import { recallRoute } from '@/lib/native/last-route'

/**
 * Redirect native app users away from the public marketing page on cold
 * launch. No one opens the app to see a landing page — they need login,
 * the dashboard, or whatever deep surface they were last on.
 *
 * Resolution order (first match wins):
 *   1. Last-known route from Capacitor Preferences (deep restoration —
 *      same pattern Linear / Notion / Stripe Dashboard use). Only honored
 *      if the user is logged in (`dashboardUrl` non-null) — otherwise the
 *      cached route would land them on a private route they no longer
 *      have access to and the route guard would bounce them anyway.
 *   2. `dashboardUrl` (the user's primary org root, computed server-side).
 *   3. `/login` (cold launch as unauth user).
 *
 * Props:
 *   - dashboardUrl: if user is logged in, redirect here as fallback
 */
export function NativeRedirect({ dashboardUrl }: { dashboardUrl: string | null }) {
  const router = useRouter()

  useEffect(() => {
    if (!isNativePlatform()) return

    if (!dashboardUrl) {
      // Unauth — go to login. Don't honor cached route; sign-out should
      // have called `forgetRoute()` but defense in depth.
      router.replace('/login')
      return
    }

    // Auth — try the cached deep route first, fall back to dashboard root.
    let cancelled = false
    void recallRoute().then((cached) => {
      if (cancelled) return
      router.replace(cached ?? dashboardUrl)
    })
    return () => {
      cancelled = true
    }
  }, [dashboardUrl, router])

  // Render nothing — this component only produces side effects.
  return null
}
